import { Menu } from "@mui/icons-material";
import {
  AppBar,
  Box,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
} from "@mui/material";
import {
  FunctionComponent,
  ReactNode,
  useCallback,
  useEffect,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { routerConfig } from "../../router";
import { ENV_CONFIG } from "../../utils/constants";
import useStore from "../../store";
import "./style.scss";

interface MainLayoutProps {
  children: ReactNode;
  title: string;
}

let isUserEvaluated = false;

const MainLayout: FunctionComponent<MainLayoutProps> = ({
  children,
  title,
}) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState<boolean>(false);
  const { setCurrentUserInfo, currentUserInfo, setEnums } = useStore();
  const isSuperUser = currentUserInfo?.is_superuser;

  const toggleDrawer = (newOpen: boolean) => () => {
    setOpen(newOpen);
  };

  const fetchAndSetItems = useCallback(async () => {
    try {
      if (isUserEvaluated) {
        return;
      }

      const response = await fetch(
        `${ENV_CONFIG.BACKEND_BASE_URL}/api/authn/current-user/`,
        { credentials: "include" },
      );
      const data = await response.json();

      isUserEvaluated = true;
      setCurrentUserInfo(data);
    } catch (error) {
      console.log("error: ", error);
    }
  }, [setCurrentUserInfo]);

  const evaluateAuth = useCallback(() => {
    if (isUserEvaluated && !currentUserInfo?.username) {
      navigate(`/api/admin/`);
    }
  }, [currentUserInfo, navigate]);

  const fetchAndLoadEnums = useCallback(async () => {
    try {
      const query = `${ENV_CONFIG.BACKEND_BASE_URL}/api/commons/enums/?types=ITEM_SIZE,ORDER_SOURCE,PAYMENT_MODE,EXPENSE_SOURCE,EXPIRY_CONFIG`;
      const response = await fetch(query);
      const result = await response.json();
      setEnums(result);
    } catch (error) {
      console.error("Failed to fetch order stats:", error);
    }
  }, [setEnums]);

  useEffect(() => {
    evaluateAuth();
  }, [evaluateAuth]);

  useEffect(() => {
    fetchAndSetItems();
    fetchAndLoadEnums();
  }, [fetchAndSetItems, fetchAndLoadEnums]);

  const DrawerList = (
    <Box sx={{ width: 300 }} onClick={toggleDrawer(false)}>
      <Box
        sx={{
          backgroundColor: "#000",
          color: "#fff",
          height: 60,
          alignContent: "center",
        }}
      >
        <Typography variant="h4" textAlign={"center"} fontWeight={"bolder"}>
          SPLASH
        </Typography>
      </Box>

      <List>
        {routerConfig
          .filter(({ isAdminPage }) => !isAdminPage)
          .map(
            ({ path, title, IconComp, hidden }) =>
              !hidden && (
                <ListItem
                  key={path}
                  disablePadding
                  onClick={() => navigate(path)}
                >
                  <ListItemButton>
                    <ListItemIcon>
                      <IconComp />
                    </ListItemIcon>
                    <ListItemText primary={title} />
                  </ListItemButton>
                </ListItem>
              ),
          )}

        {isSuperUser && (
          <>
            <Divider />
            {routerConfig
              .filter(({ isAdminPage }) => isAdminPage)
              .map(
                ({ path, title, IconComp, hidden }) =>
                  !hidden && (
                    <ListItem
                      key={path}
                      disablePadding
                      onClick={() => navigate(path)}
                    >
                      <ListItemButton>
                        <ListItemIcon>
                          <IconComp />
                        </ListItemIcon>
                        <ListItemText primary={title} />
                      </ListItemButton>
                    </ListItem>
                  ),
              )}
          </>
        )}
      </List>
    </Box>
  );

  return (
    <Box id="main-layout">
      <div>
        <Drawer open={open} onClose={toggleDrawer(false)}>
          {DrawerList}
        </Drawer>
      </div>

      <AppBar position="static" variant="elevation">
        <Toolbar>
          <IconButton
            size="large"
            edge="start"
            color="inherit"
            aria-label="menu"
            sx={{ mr: 2 }}
            onClick={toggleDrawer(true)}
          >
            <Menu onClick={toggleDrawer(true)} />
          </IconButton>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            {title}
          </Typography>
        </Toolbar>
      </AppBar>
      {children}
    </Box>
  );
};

export default MainLayout;
